import {
  loadDataThen,
  loadingClearAction,
  loadingFailAction,
  loadingStartAction,
  loadingSuccessAction,
} from "@wisr/common";
import dayjs from "dayjs";
import { Finance } from "financejs";
import { navigate } from "gatsby";
import { Epic } from "redux-observable";
import { concat, EMPTY, of } from "rxjs";
import {
  catchError,
  concatMap,
  map,
  mapTo,
  tap,
  withLatestFrom,
} from "rxjs/operators";

import { customerSaveAction } from "../customer/customer.actions";
import {
  Events,
  UNEXPECTED_ERROR,
} from "../shared/analytics/analytics.constants";
import { quoteAmplitudeHelper } from "../shared/analytics/analytics.utils";
import { redirect } from "../shared/browser.utils";
import { noopAction } from "../shared/common.actions";
import {
  convertDecimalRateToFraction,
  roundToTwoDecimals,
} from "../shared/maths.utils";
import { DASH_URL } from "../shared/url.constants";
import { AppAction, AppStore } from "../store";
import { Estimate, Offer } from "./estimate";
import {
  EstimateAcceptAction,
  EstimateAcceptActionType,
  EstimateAcceptAmplitudeEventAction,
  EstimateAcceptAmplitudeEventActionType,
  estimateAcceptSuccessAction,
  EstimateCreateAction,
  EstimateCreateActionType,
  EstimateGetAction,
  EstimateGetActionType,
  estimateGetSuccessAction,
  estimateSaveExtrasAction,
  EstimateSetAmountAction,
  EstimateSetAmountActionType,
  EstimateSetLoantermAction,
  EstimateSetLoantermActionType,
  EstimateUpdateQuoteJourneyAction,
  EstimateUpdateQuoteJourneyActionType,
  estimateUpdateRepaymentAction,
} from "./estimate.actions";
import {
  createEstimate,
  estimateAccepted,
  getEstimateById,
  sendAmplitudeEvent,
  setLoanterm,
  updateLoanAmount,
  updateQuoteJourney,
} from "./estimate.service";

export const GET_ESTIMATE_LOADING = "GET_ESTIMATE_LOADING";
export const getEstimateEpic: Epic<AppAction> = (actions) =>
  actions
    .ofType<EstimateGetAction>(EstimateGetActionType)
    .pipe(
      loadDataThen(GET_ESTIMATE_LOADING, (action) =>
        getEstimateById(action.estimateId, action.bid).pipe(
          concatMap((loanOffer) => [
            customerSaveAction(loanOffer.customer),
            estimateGetSuccessAction(loanOffer.estimate),
            loadingSuccessAction(GET_ESTIMATE_LOADING),
          ])
        )
      )
    );

export const CREATE_ESTIMATE_LOADING = "CREATE_ESTIMATE_LOADING";
export const createEstimateEpic: Epic<AppAction> = (actions) =>
  actions.ofType<EstimateCreateAction>(EstimateCreateActionType).pipe(
    concatMap((action) =>
      concat(
        of(loadingClearAction(GET_ESTIMATE_LOADING)),
        of(loadingStartAction(CREATE_ESTIMATE_LOADING)),
        of(
          estimateSaveExtrasAction({
            purpose: action.quoteRequest.loanPurpose || "",
            assetKnown: action.quoteRequest.securedAssetDetails?.isKnown,
            saleType: action.quoteRequest.securedAssetDetails?.saleType,
            assetAproxAge:
              action.quoteRequest.securedAssetDetails?.approximateAge,
            assetYearOfManufacture:
              action.quoteRequest.securedAssetDetails?.yearOfManufacture,
          })
        ),
        createEstimate(action.quoteRequest, action.wcToken).pipe(
          tap((createEstimateResult) =>
            navigate(
              `/estimate?appid=${createEstimateResult.wisrApplicationId}${
                createEstimateResult.partnerCompanyId
                  ? `&bid=${createEstimateResult.partnerCompanyId}`
                  : ""
              }`
            )
          ),
          mapTo(loadingSuccessAction(CREATE_ESTIMATE_LOADING)),
          // @TODO: Should this error mapping logic live here? Is there consistency in how our APIs handle errors?
          catchError((e) => {
            if (e.response && e.response.data && e.response.data.errorFields) {
              quoteAmplitudeHelper.sendEvent(Events.SERVER_ERROR, {
                Type: "Quote Form",
                Title: e.response.data.title,
                Code: e.response.data.status,
                Detail: e.response.data.detail,
                Error: e.response.data.errorFields,
                Page: window.location.href,
              });

              interface ErrorDetail {
                errorMessage: string[];
                fieldName: string;
              }

              const reasons = (
                Object.values(e.response.data.errorFields) as ErrorDetail[]
              )
                .filter((error) => {
                  if (error.fieldName && error.errorMessage[0]) {
                    return true;
                  }
                  return false;
                })
                .map((error) => {
                  return `${error.fieldName.split(".").slice(-1)[0]}: ${
                    error.errorMessage[0]
                  }`;
                });
              return of(
                loadingFailAction(CREATE_ESTIMATE_LOADING, "Error", { reasons })
              );
            }
            quoteAmplitudeHelper.sendEvent(Events.SERVER_ERROR, {
              Type: "Quote Form",
              Error: `${e}`,
              Page: window.location.href,
            });
            console.log(e.toString() === "Error: Network Error");
            const isNetworkError = e.toString() === "Error: Network Error";
            return of(
              loadingFailAction(CREATE_ESTIMATE_LOADING, UNEXPECTED_ERROR, {
                originalError: `${e}${
                  isNetworkError
                    ? ", please try again or try on a different device."
                    : ""
                }\n${dayjs().format()}`,
              })
            );
          })
        )
      )
    ),
    catchError(
      /* istanbul ignore next */ () => {
        quoteAmplitudeHelper.sendEvent(Events.SERVER_ERROR, {
          Type: "Quote Form",
          Error: UNEXPECTED_ERROR,
          Page: window.location.href,
        });
        return of(loadingFailAction(CREATE_ESTIMATE_LOADING, UNEXPECTED_ERROR));
      }
    )
  );

export const SET_LOANTERM_LOADING = "SET_LOANTERM_LOADING";
export const setLoantermEpic: Epic<AppAction, AppAction, AppStore> = (
  actions,
  states
) =>
  actions.ofType<EstimateSetLoantermAction>(EstimateSetLoantermActionType).pipe(
    withLatestFrom(states),
    // @TODO: We know this value SHOULD always be defined, but it's still probably best to handle the potential error state of an undefined estimate
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    map(([action, state]) => [action, state.estimateState.estimate!] as const),
    concatMap(([action, estimate]) =>
      concat(
        of(loadingStartAction(SET_LOANTERM_LOADING)),
        setLoanterm(estimate.loanId, estimate.quoteId, action.loanterm).pipe(
          tap(() => {
            if (action.estimateAcceptResponse?.email) {
              if (action.editing) {
                redirect(
                  `${DASH_URL}/application/review-your-details?term=${
                    action.loanterm / 12
                  }`
                );
              } else {
                redirect(
                  `${DASH_URL}/application?email=${action.estimateAcceptResponse.email.replace(
                    "+",
                    "%2B"
                  )}&wisrapplicationid=${
                    action.estimateAcceptResponse.wisrApplicationId
                  }&firstname=${action.firstName}`
                );
              }
            }
          }),
          map(noopAction)
        ),
        of(loadingSuccessAction(SET_LOANTERM_LOADING))
      )
    )
  );

export const SET_AMOUNT_LOADING = "SET_AMOUNT_LOADING";
export const setAmountEpic: Epic<AppAction, AppAction, AppStore> = (
  actions,
  states
) =>
  actions.ofType<EstimateSetAmountAction>(EstimateSetAmountActionType).pipe(
    withLatestFrom(states),
    // @TODO: We know this value SHOULD always be defined, but it's still probably best to handle the potential error state of an undefined estimate
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    map(([action, state]) => [action, state.estimateState.estimate!] as const),
    concatMap(([action, estimate]) =>
      concat(
        of(loadingStartAction(SET_AMOUNT_LOADING)),
        of(updateAmount(estimate, action.loanAmount)).pipe(
          map((updatedEstimate) => recalculatePMT(updatedEstimate)),
          concatMap((updatedEstimate) =>
            concat(
              of(estimateUpdateRepaymentAction(updatedEstimate)),
              updateLoanAmount(updatedEstimate).pipe(
                mapTo(loadingSuccessAction(SET_AMOUNT_LOADING)),
                catchError((error) =>
                  of(loadingFailAction(SET_AMOUNT_LOADING, error.message))
                )
              )
            )
          )
        )
      )
    )
  );

export const UPDATE_QUOTE_JOURNEY_LOADING = "UPDATE_QUOTE_JOURNEY_LOADING";
export const updateQuoteJourneyEpic: Epic<AppAction> = (actions) =>
  actions
    .ofType<EstimateUpdateQuoteJourneyAction>(
      EstimateUpdateQuoteJourneyActionType
    )
    .pipe(
      concatMap((action) =>
        concat(
          of(loadingStartAction(UPDATE_QUOTE_JOURNEY_LOADING)),
          updateQuoteJourney(action.quoteId).pipe(
            mapTo(loadingSuccessAction(UPDATE_QUOTE_JOURNEY_LOADING)),
            catchError((error) =>
              of(loadingFailAction(UPDATE_QUOTE_JOURNEY_LOADING, error.message))
            )
          )
        )
      )
    );

export const ACCEPT_ESTIMATE_LOADING = "ACCEPT_ESTIMATE_LOADING";
export const acceptEstimateEpic: Epic<AppAction> = (actions) =>
  actions.ofType<EstimateAcceptAction>(EstimateAcceptActionType).pipe(
    concatMap((action) =>
      concat(
        of(loadingStartAction(ACCEPT_ESTIMATE_LOADING)),
        estimateAccepted(action.quoteId, action.term).pipe(
          concatMap((estimateAcceptedResponse) => [
            estimateAcceptSuccessAction(estimateAcceptedResponse),
            loadingSuccessAction(ACCEPT_ESTIMATE_LOADING),
          ]),
          catchError((error) =>
            of(loadingFailAction(ACCEPT_ESTIMATE_LOADING, error.message))
          )
        )
      )
    )
  );

function recalculatePMT(estimate: Estimate): Estimate {
  const finance = new Finance();

  const offers: Offer[] = estimate.offers.map((offer) => {
    const rate = convertDecimalRateToFraction(offer.rate, 2);
    const repayment = finance.PMT(
      rate / 12,
      offer.term,
      -Math.abs(estimate.loanAmount + estimate.totalEstablishmentFee)
    );
    offer.repayment = roundToTwoDecimals(repayment);
    return offer;
  });

  return { ...estimate, offers };
}

function updateAmount(estimate: Estimate, loanAmount: number): Estimate {
  return { ...estimate, loanAmount };
}

export const sendAcceptAmplitudeEventEpic: Epic<AppAction> = (actions) =>
  actions
    .ofType<EstimateAcceptAmplitudeEventAction>(
      EstimateAcceptAmplitudeEventActionType
    )
    .pipe(
      concatMap((action) =>
        sendAmplitudeEvent(action.wisrApplicationId, action.eventName, {
          isPrefill: action.isPrefill,
        }).pipe(
          mapTo(noopAction()),
          catchError((error) => {
            console.warn(
              "Failed to send rate estimate send amplitude event",
              error
            );
            return EMPTY;
          })
        )
      )
    );
